/**
 * SpatialOperationsPanel
 *
 * Side panel for spatial operations on a selected polygon:
 * - Buffer (expand/shrink)
 * - Simplify (reduce vertices)
 * - Proximity (find nearby polygons)
 * - Spatial stats (area, perimeter, vertices, compactness)
 * - Per-polygon overlap check
 */

import { useState, useEffect } from 'react';
import {
    X,
    Maximize2,
    Minimize2,
    MapPin,
    Layers,
    Activity,
    Loader2,
    Save,
    BarChart3,
    Navigation,
    AlertTriangle,
    CheckCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { spatialApi, getErrorMessage } from '../../lib/api';

interface SpatialOperationsPanelProps {
    polygon: {
        id: string;
        name: string;
        area_ha?: number;
        commodity?: string;
        producer_name?: string;
        region?: string;
        country?: string;
    };
    onClose: () => void;
    onBufferPreview?: (geometry: object | null) => void;
    onSimplifyPreview?: (geometry: object | null) => void;
    onNearbyHighlight?: (polygonIds: string[]) => void;
    onOverlapsFound?: (overlaps: any[]) => void;
    onPolygonCreated?: () => void;
}

type ActiveTab = 'stats' | 'buffer' | 'simplify' | 'proximity' | 'overlaps';

export default function SpatialOperationsPanel({
    polygon,
    onClose,
    onBufferPreview,
    onSimplifyPreview,
    onNearbyHighlight,
    onOverlapsFound,
    onPolygonCreated,
}: SpatialOperationsPanelProps) {
    const [activeTab, setActiveTab] = useState<ActiveTab>('stats');
    const [loading, setLoading] = useState(false);

    // Stats
    const [stats, setStats] = useState<any>(null);

    // Buffer
    const [bufferDistance, setBufferDistance] = useState(50);
    const [bufferResult, setBufferResult] = useState<any>(null);
    const [saveAsNew, setSaveAsNew] = useState(false);
    const [newName, setNewName] = useState('');

    // Simplify
    const [tolerance, setTolerance] = useState(5);
    const [simplifyResult, setSimplifyResult] = useState<any>(null);
    const [simplifySaveAsNew, setSimplifySaveAsNew] = useState(false);
    const [simplifyNewName, setSimplifyNewName] = useState('');

    // Proximity
    const [maxDistance, setMaxDistance] = useState(1000);
    const [proximityResult, setProximityResult] = useState<any>(null);

    // Overlaps
    const [overlapsResult, setOverlapsResult] = useState<any>(null);

    // Load stats on mount
    useEffect(() => {
        loadStats();
    }, [polygon.id]);

    // Clear previews on tab change
    useEffect(() => {
        onBufferPreview?.(null);
        onSimplifyPreview?.(null);
        onNearbyHighlight?.([]);
        setBufferResult(null);
        setSimplifyResult(null);
        setProximityResult(null);
        setOverlapsResult(null);
    }, [activeTab]);

    const loadStats = async () => {
        try {
            const data = await spatialApi.getSpatialStats(polygon.id);
            setStats(data);
        } catch {
            // Stats are non-critical
        }
    };

    const handleBuffer = async () => {
        setLoading(true);
        try {
            const result = await spatialApi.bufferPolygon(polygon.id, {
                distance_m: bufferDistance,
                save_as_new: saveAsNew,
                new_name: newName || undefined,
            });
            setBufferResult(result);
            onBufferPreview?.(result.buffered_geometry);

            if (result.saved_polygon_id) {
                toast.success(`Buffer guardado como nuevo polígono`);
                onPolygonCreated?.();
            } else {
                toast.success(`Buffer calculado: ${result.buffered_area_ha} ha`);
            }
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al calcular buffer');
        } finally {
            setLoading(false);
        }
    };

    const handleSimplify = async () => {
        setLoading(true);
        try {
            const result = await spatialApi.simplifyPolygon(polygon.id, {
                tolerance_m: tolerance,
                save_as_new: simplifySaveAsNew,
                new_name: simplifyNewName || undefined,
            });
            setSimplifyResult(result);
            onSimplifyPreview?.(result.simplified_geometry);

            if (result.saved_polygon_id) {
                toast.success(`Polígono simplificado guardado`);
                onPolygonCreated?.();
            } else {
                toast.success(`Simplificado: ${result.vertices_removed} vértices eliminados (${result.reduction_percentage}%)`);
            }
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al simplificar');
        } finally {
            setLoading(false);
        }
    };

    const handleProximity = async () => {
        setLoading(true);
        try {
            const result = await spatialApi.findNearby(polygon.id, { max_distance_m: maxDistance });
            setProximityResult(result);
            const nearbyIds = result.nearby_polygons?.map((p: any) => p.polygon_id) || [];
            onNearbyHighlight?.(nearbyIds);
            if (result.total_nearby === 0) {
                toast.success('No se encontraron polígonos cercanos');
            } else {
                toast.success(`${result.total_nearby} polígono(s) cercano(s)`);
            }
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al buscar proximidad');
        } finally {
            setLoading(false);
        }
    };

    const handleOverlaps = async () => {
        setLoading(true);
        try {
            const result = await spatialApi.detectSingleOverlaps(polygon.id);
            setOverlapsResult(result);
            onOverlapsFound?.(result.overlaps || []);
            if (result.total_overlaps === 0) {
                toast.success('No se detectaron superposiciones');
            } else {
                toast.error(`${result.total_overlaps} superposición(es): ${result.total_overlap_area_ha} ha`);
            }
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al detectar superposiciones');
        } finally {
            setLoading(false);
        }
    };

    const tabs: { id: ActiveTab; label: string; icon: any }[] = [
        { id: 'stats', label: 'Stats', icon: BarChart3 },
        { id: 'buffer', label: 'Buffer', icon: Maximize2 },
        { id: 'simplify', label: 'Simplificar', icon: Activity },
        { id: 'proximity', label: 'Proximidad', icon: Navigation },
        { id: 'overlaps', label: 'Solapamiento', icon: Layers },
    ];

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-full max-w-[95vw] sm:max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {polygon.name}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                            {polygon.area_ha} ha • {polygon.commodity}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                    <X className="w-4 h-4 text-gray-500" />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                {tabs.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setActiveTab(id)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                            activeTab === id
                                ? 'border-primary-600 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="p-4 max-h-[50vh] overflow-y-auto">
                {/* STATS TAB */}
                {activeTab === 'stats' && (
                    <div className="space-y-3">
                        {stats ? (
                            <>
                                <StatRow label="Área" value={`${stats.area_ha ?? '—'} ha`} />
                                <StatRow label="Perímetro" value={stats.perimeter_m ? `${(stats.perimeter_m / 1000).toFixed(2)} km` : '—'} />
                                <StatRow label="Vértices" value={`${stats.vertex_count}`} />
                                <StatRow label="Centroide" value={`${stats.centroid_lat?.toFixed(6)}, ${stats.centroid_lon?.toFixed(6)}`} />
                                <StatRow
                                    label="Compacidad"
                                    value={stats.compactness ? `${(stats.compactness * 100).toFixed(1)}%` : '—'}
                                    hint="100% = círculo perfecto"
                                />
                            </>
                        ) : (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                            </div>
                        )}
                    </div>
                )}

                {/* BUFFER TAB */}
                {activeTab === 'buffer' && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Distancia ({bufferDistance} m)
                            </label>
                            <input
                                type="range"
                                min={-200}
                                max={500}
                                step={10}
                                value={bufferDistance}
                                onChange={(e) => setBufferDistance(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-400">
                                <span>-200m (contraer)</span>
                                <span>+500m (expandir)</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="saveBufferNew"
                                checked={saveAsNew}
                                onChange={(e) => setSaveAsNew(e.target.checked)}
                                className="rounded"
                            />
                            <label htmlFor="saveBufferNew" className="text-xs text-gray-700 dark:text-gray-300">
                                Guardar como nuevo polígono
                            </label>
                        </div>

                        {saveAsNew && (
                            <input
                                type="text"
                                placeholder="Nombre del nuevo polígono (opcional)"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="input text-sm"
                            />
                        )}

                        <button
                            onClick={handleBuffer}
                            disabled={loading || bufferDistance === 0}
                            className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Maximize2 className="w-4 h-4" />
                            )}
                            Calcular Buffer
                        </button>

                        {bufferResult && (
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-3 text-xs space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Original:</span>
                                    <span className="font-medium">{bufferResult.original_area_ha} ha</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Con buffer:</span>
                                    <span className="font-medium">{bufferResult.buffered_area_ha} ha</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Cambio:</span>
                                    <span className={`font-medium ${bufferResult.area_change_ha >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {bufferResult.area_change_ha >= 0 ? '+' : ''}{bufferResult.area_change_ha} ha
                                    </span>
                                </div>
                                {bufferResult.saved_polygon_id && (
                                    <div className="flex items-center gap-1 text-green-600 pt-1">
                                        <Save className="w-3 h-3" />
                                        Guardado
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* SIMPLIFY TAB */}
                {activeTab === 'simplify' && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tolerancia ({tolerance} m)
                            </label>
                            <input
                                type="range"
                                min={1}
                                max={100}
                                step={1}
                                value={tolerance}
                                onChange={(e) => setTolerance(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-400">
                                <span>1m (preciso)</span>
                                <span>100m (agresivo)</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="saveSimplifyNew"
                                checked={simplifySaveAsNew}
                                onChange={(e) => setSimplifySaveAsNew(e.target.checked)}
                                className="rounded"
                            />
                            <label htmlFor="saveSimplifyNew" className="text-xs text-gray-700 dark:text-gray-300">
                                Guardar como nuevo polígono
                            </label>
                        </div>

                        {simplifySaveAsNew && (
                            <input
                                type="text"
                                placeholder="Nombre del nuevo polígono (opcional)"
                                value={simplifyNewName}
                                onChange={(e) => setSimplifyNewName(e.target.value)}
                                className="input text-sm"
                            />
                        )}

                        <button
                            onClick={handleSimplify}
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Activity className="w-4 h-4" />
                            )}
                            Simplificar
                        </button>

                        {simplifyResult && (
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-3 text-xs space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Vértices originales:</span>
                                    <span className="font-medium">{simplifyResult.original_vertices}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Vértices simplificados:</span>
                                    <span className="font-medium">{simplifyResult.simplified_vertices}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Reducción:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        -{simplifyResult.vertices_removed} ({simplifyResult.reduction_percentage}%)
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Diferencia área:</span>
                                    <span className="font-medium">{simplifyResult.area_difference_ha} ha</span>
                                </div>
                                {simplifyResult.saved_polygon_id && (
                                    <div className="flex items-center gap-1 text-green-600 pt-1">
                                        <Save className="w-3 h-3" />
                                        Guardado
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* PROXIMITY TAB */}
                {activeTab === 'proximity' && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Radio de búsqueda ({maxDistance >= 1000 ? `${(maxDistance / 1000).toFixed(1)} km` : `${maxDistance} m`})
                            </label>
                            <input
                                type="range"
                                min={100}
                                max={10000}
                                step={100}
                                value={maxDistance}
                                onChange={(e) => setMaxDistance(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-400">
                                <span>100m</span>
                                <span>10km</span>
                            </div>
                        </div>

                        <button
                            onClick={handleProximity}
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Navigation className="w-4 h-4" />
                            )}
                            Buscar Cercanos
                        </button>

                        {proximityResult && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-500">
                                    {proximityResult.total_nearby} polígono(s) dentro de{' '}
                                    {maxDistance >= 1000
                                        ? `${(maxDistance / 1000).toFixed(1)} km`
                                        : `${maxDistance} m`}
                                </p>
                                {proximityResult.nearby_polygons?.map((p: any) => (
                                    <div
                                        key={p.polygon_id}
                                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg text-xs"
                                    >
                                        <div>
                                            <span className="font-medium">{p.polygon_name}</span>
                                            <span className="text-gray-500 ml-1">({p.commodity})</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-medium text-gray-900 dark:text-white">{p.distance_m} m</div>
                                            <div className="text-gray-500">{p.area_ha} ha</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* OVERLAPS TAB */}
                {activeTab === 'overlaps' && (
                    <div className="space-y-4">
                        <p className="text-xs text-gray-500">
                            Detecta si este polígono se superpone con otros del tenant.
                        </p>

                        <button
                            onClick={handleOverlaps}
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Layers className="w-4 h-4" />
                            )}
                            Detectar Superposiciones
                        </button>

                        {overlapsResult && (
                            <div className="space-y-2">
                                {overlapsResult.total_overlaps === 0 ? (
                                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-xs text-green-700">
                                        <CheckCircle className="w-4 h-4" />
                                        Sin superposiciones
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-xs text-red-700">
                                            <AlertTriangle className="w-4 h-4" />
                                            {overlapsResult.total_overlaps} superposición(es) •{' '}
                                            {overlapsResult.total_overlap_area_ha} ha total
                                        </div>
                                        {overlapsResult.overlaps?.map((o: any, idx: number) => {
                                            const otherName = o.polygon_a_id === polygon.id
                                                ? o.polygon_b_name
                                                : o.polygon_a_name;
                                            const otherPct = o.polygon_a_id === polygon.id
                                                ? o.overlap_percentage_a
                                                : o.overlap_percentage_b;
                                            return (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-2 bg-red-50/50 dark:bg-red-900/10 rounded-lg text-xs"
                                                >
                                                    <span className="font-medium">{otherName}</span>
                                                    <div className="text-right">
                                                        <div className="font-medium text-red-600">{o.overlap_area_ha} ha</div>
                                                        <div className="text-gray-500">{otherPct}%</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function StatRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
            <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                {hint && <span className="text-[10px] text-gray-400 ml-1">({hint})</span>}
            </div>
            <span className="text-xs font-medium text-gray-900 dark:text-white">{value}</span>
        </div>
    );
}
